import React, { useEffect, useMemo, useRef } from "react";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { Button, Modal, Typography } from "@thingsw/pitta-design-system";
import { useMediaQuery } from "@material-ui/core";
import clsx from "clsx";

import { Webviewer } from "@thingsw/pitta-modules";
import ArrowBack from "@material-ui/icons/ArrowBack";
import useRole from "../../hooks/useRole";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme: Theme) => ({
  modalBottom: {
    justifyContent: "space-between",
    padding: theme.spacing(2),
  },
  modalContentWrap: {
    border: "none",
    padding: theme.spacing(2),
    paddingTop: 9,
    flexDirection: "column",
  },
  step0Content: {
    justifyContent: "center",
  },
  contentWrap: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  title: {
    marginTop: theme.spacing(4) + 1,
    textAlign: "center",
    [theme.breakpoints.up(Webviewer.mobile)]: {
      marginTop: theme.spacing(4) - 1,
      textAlign: "inherit",
    },
  },
  content: {
    marginTop: theme.spacing(2) + 2,
    textAlign: "center",
    [theme.breakpoints.up(Webviewer.mobile)]: {
      textAlign: "inherit",
    },
  },
  webBtnDiv: {
    marginTop: theme.spacing(5.75),
    display: "flex",
    width: "100%",
    justifyContent: "flex-end",
  },
}));

interface OnboardingMobileProps {
  step: number;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
}

export const FleetaOnboardingMobile = ({
  step,
  onNext,
  onClose,
  onBack,
}: OnboardingMobileProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));

  const { isDriver } = useRole();

  const DONE_STEP = isDriver ? 2 : 5;

  const titleMarkup = useMemo(() => {
    if (step === 0) {
      return "Camera’s features";
    } else if (step === 1) {
      return "Toggle to switch_";
    } else if (step === 2) {
      return "Cloud Storage";
    } else if (step === 3) {
      return "GPS Tracking";
    } else if (step === 4) {
      return "Geofences";
    } else if (step === 5) {
      return "Manage Fleet members";
    } else {
      return "";
    }
  }, [step]);

  useEffect(() => {
    if (videoRef.current) {
      setIsVideoLoaded(false);
      videoRef.current.load();
    }
  }, [step]);

  const videoUrl = useMemo(() => {
    if (step === 0) {
      return "/video/onboarding1.mp4";
    } else if (step === 1) {
      return "/video/onboarding2.mp4";
    } else if (step === 2) {
      return "/video/onboarding3.mp4";
    } else if (step === 3) {
      return "/video/onboarding4.mp4";
    } else if (step === 4) {
      return "/video/onboarding5.mp4";
    } else if (step === 5) {
      return "/video/onboarding6.mp4";
    } else {
      return "";
    }
  }, [step]);

  const contentMarkup = useMemo(() => {
    if (step === 0) {
      return <Typography variant="Body">{t("In the cloud_")}</Typography>;
    } else if (step === 1) {
      return (
        <>
          <Typography variant="Body">{t("Check the current_")}</Typography>
          <Typography variant="Body">{t("In List View_")}</Typography>
        </>
      );
    } else if (step === 2) {
      return (
        <Typography variant="Body">
          {t(
            "클라우드 스토리지 관련해서 탭이 새로 생겼고 제공되는 것에 대해 안내해야 합니다."
          )}
        </Typography>
      );
    } else if (step === 3) {
      return (
        <Typography variant="Body">{t("Visualize your vehicle’s_")}</Typography>
      );
    } else if (step === 4) {
      return (
        <Typography variant="Body">{t("Create geofences and2_")}</Typography>
      );
    } else if (step === 5) {
      return (
        <Typography variant="Body">{t("Invite fleet members_")}</Typography>
      );
    }
  }, [step, t]);

  return (
    <Modal
      open
      mobile={mobile}
      actionClassName={classes.modalBottom}
      contentClassName={clsx(
        classes.modalContentWrap,
        step === 0 && classes.step0Content
      )}
      content={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100vh",
          }}
        >
          <div
            style={{
              paddingTop: 36,
            }}
          >
            <video
              ref={videoRef}
              onLoadedData={() => setIsVideoLoaded(true)}
              style={{
                width: "100%",
                height: "100%",
                maxHeight: 343,
                objectFit: "cover",
                boxSizing: "border-box",
                borderRadius: 12,
                display: isVideoLoaded ? "block" : "none",
                marginBottom: 16,
              }}
              autoPlay
              muted
              loop
              playsInline
              webkit-playsinline
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
            {!isVideoLoaded && (
              <Skeleton
                variant="rect"
                style={{
                  width: "100%",
                  height: 343,
                  marginBottom: 16,
                  borderRadius: 12,
                }}
              ></Skeleton>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <Typography variant="H6">{t(titleMarkup)}</Typography>
              {contentMarkup}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div>
              {step !== 0 && (
                <Button
                  variant="text"
                  onClick={onBack}
                  color="gray"
                  startIcon={<ArrowBack />}
                >
                  {t("Back")}
                </Button>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: 16,
              }}
            >
              {DONE_STEP === step ? (
                <></>
              ) : (
                <Button onClick={onClose} variant="text" color="gray">
                  {t("Skip tour")}
                </Button>
              )}

              <Button
                onClick={onNext}
                variant="contained"
                color="primary"
                style={{
                  padding: "8px 0",
                  minWidth: 58,
                }}
              >
                {DONE_STEP === step ? t("Done") : t("Next")}
              </Button>
            </div>
          </div>
        </div>
      }
      btnNoMargin={step === 0}
      fullSize={mobile}
    />
  );
};
